import { useEffect, useRef, useState } from 'react'
import { BrandMark } from '../components/BrandMark.js'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '../i18n/context.js'
import { clearPendingReturn, loginUrl, readPendingReturn, rememberPendingReturn, useSession } from '../auth/session.js'
import { safeReturnPath } from '../routes.js'
import { Banner, Button, LinearProgress } from '../ui/primitives.js'

/**
 * There is nothing to type here on purpose. Access is single sign-on on
 * `security.miglioresoftware.com` and the CMS has **no login of its own**
 * (document/10 RF-60): one button, which leaves for Security and comes back
 * with a session — to the page the person asked for (`?ritorno=`).
 *
 * Two things still happen on this screen. The silent check — `prompt=none`,
 * once per tab — turns an existing Security session into a CMS session with
 * nothing on screen (document/40 §1.9 point 9); when it finds nobody signed
 * in, the gateway sends the person back here **without** an `error`, because
 * "you have not signed in yet" is the normal state of a first visit and must
 * not look like a fault. A callback that really did fail lands here with
 * `?error=`, which becomes a sentence a person can act on — plus the code in
 * small print, so nobody is ever left guessing what to report.
 */

const SILENT_CHECK_FLAG = 'cms.sso.silentCheckDone'

function silentCheckAlreadyDone(): boolean {
  try {
    return window.sessionStorage.getItem(SILENT_CHECK_FLAG) === '1'
  } catch {
    // Storage disabled: skip the silent check rather than risk a redirect loop.
    return true
  }
}

function markSilentCheckDone(): void {
  try {
    window.sessionStorage.setItem(SILENT_CHECK_FLAG, '1')
  } catch {
    // Nothing to do: the flag is a convenience, not state we depend on.
  }
}

/** Security names the failure; we answer with a sentence, and fall back when we have none for it. */
function messageForError(t: (key: string) => string, code: string): string {
  const key = `auth.errors.${code}`
  const message = t(key)
  return message === key ? t('auth.errors.generic') : message
}

export function LoginScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { state, signIn } = useSession()
  const [error] = useState<string | null>(() => searchParams.get('error'))
  // The URL says where to go back; after the silent check the gateway returns
  // here bare, and the page the person asked for survives in the tab's storage.
  const [returnTo] = useState(() => safeReturnPath(searchParams.get('ritorno') ?? readPendingReturn()))
  const leaving = useRef(false)

  useEffect(() => {
    if (state.status === 'signed-in') {
      clearPendingReturn()
      navigate(returnTo, { replace: true })
      return
    }
    if (state.status !== 'anonymous' || leaving.current) return

    // An `error` means we have just come back from a callback that did not
    // work: another automatic attempt would loop.
    if (error || silentCheckAlreadyDone()) return
    markSilentCheckDone()
    rememberPendingReturn(returnTo)
    leaving.current = true
    window.location.assign(loginUrl(returnTo, 'none'))
  }, [state, error, navigate, returnTo])

  return <div className="login-screen">
    <main className="login-card">
      <div className="login-brand">
        <BrandMark large />
      </div>
      {state.status === 'loading' ? <>
        <h1 className="login-title">{t('auth.checking')}</h1>
        <LinearProgress label={t('auth.checking')}/>
      </> : <>
        <h1 className="login-title">{t('auth.title')}</h1>
        <p className="login-lead">{t('auth.description')}</p>

        {error && <Banner tone="error" title={t('auth.invalidTitle')}>
          <p>{messageForError(t, error)}</p>
          <p className="muted">{t('auth.errors.code', { code: error })}</p>
        </Banner>}

        {state.status === 'unreachable' && <Banner tone="error" title={t('auth.networkTitle')}>{state.message}</Banner>}

        {returnTo !== '/' && <p className="login-return">{t('auth.returnHint')}</p>}

        <Button variant="filled" trailingIcon="open_in_new" className="login-cta" onClick={() => signIn(returnTo)}>{t('auth.submit')}</Button>
        <p className="login-foot">{t('auth.footnote')}</p>
      </>}
    </main>
  </div>
}
